import React, { cloneElement, createRef, useEffect } from 'react';

import { useSnackbar } from 'notistack';
import { FieldValues, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { useGetS3Url, useHandleUploadFile } from '../commonFormsHooks';
import { FileUploadFieldRendererProps } from '../FieldRenderers';
import FormFieldDescription from '../FormFieldDescription';

const imageTypes = ['image/jpg', 'image/jpeg', 'image/png'];

const S3FileField = <T extends FieldValues>({
  label,
  context,
  subtitle,
  onChange,
  required,
  inputProps,
  triggerBtn,
  displaySnackbarOnSuccess = true,
  setIsLoading,
  ...props
}: FileUploadFieldRendererProps<T>) => {
  const { t } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const { field } = useController(props);
  const ref = createRef<HTMLInputElement>();
  const handleGetS3Url = useGetS3Url();
  const handleUpload = useHandleUploadFile();

  useEffect(() => {
    const { value } = field;

    if (!value || !value.includes('http') || !onChange) {
      return;
    }
    // after user uploads the picture, this will update the form value with the s3 key
    onChange(field.value);
  }, [field.value]);

  const onFilesChange = () => {
    if (!ref.current || !ref.current.files || ref.current.files.length === 0) {
      return;
    }

    const file = ref.current.files[0];

    handleGetS3Url(file.name, context).then((res) => {
      if (setIsLoading) setIsLoading(true);

      handleUpload(file, res.url)
        .then(() => {
          if (onChange) {
            onChange(res, file);
          }

          if (displaySnackbarOnSuccess) {
            enqueueSnackbar(
              `${file.name} ${t(
                'common.uploadSuccess',
                'uploaded successfully',
              )}`,
              {
                variant: 'success',
              },
            );
          }
        })
        .catch((err) => {
          enqueueSnackbar(err.message, { variant: 'error' });
        })
        .finally(() => {
          if (setIsLoading) setIsLoading(false);
        });
    });
  };

  return (
    <Box sx={{ display: 'grid' }}>
      <FormFieldDescription
        required={required}
        title={label}
        subtitle={subtitle}
      />
      {triggerBtn ? (
        cloneElement(triggerBtn, { onClick: () => ref.current?.click() })
      ) : (
        <Button
          startIcon={<CloudUploadIcon />}
          onClick={() => ref.current?.click()}
        >
          {t('common.upload', 'Upload')}
        </Button>
      )}
      <input
        ref={ref}
        type="file"
        accept={imageTypes.join(', ')}
        hidden
        onInput={onFilesChange}
        {...inputProps}
      />
    </Box>
  );
};

export default S3FileField;
